import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';

type Env = 'development' | 'staging' | 'demo' | 'production';

type IGetEnvFeatureFlag = () => {
  env: Env;
  enableDevelopment: boolean;
  enableStaging: boolean;
  /**
   * @deprecated テナントコードのハードコード（BYM/LEF/SHL）に依存するフラグ。
   * テナント単位の機能出し分けは PartnerFeatureFlag へ移行済みのため、新規利用は禁止（MMT-156）。
   * アクティベーションコード表示は `isPartnerFeatureFlagEnabled(flags, 'activation_code')`（`utils/isPartnerFeatureFlagEnabled`）を参照すること。
   * 既存参照は MMT-156 で移行済み（残参照ゼロ）。本フラグ本体および CLAUDE.md 等の記載の削除は LEA-3515 で対応する。
   */
  enableInternal: boolean;
};

const STAGING_PATTERN = /^(?:stg|qa-\d+)\.fukuri-partner-admin-dev\.jp$/;
const DEMO_HOSTNAME = 'demo.fukuri-partner-admin-dev.jp';
const PRODUCTION_HOSTNAMES = ['partner.fukuri-app.jp', 'secure.partner.fukuri-app.jp'];
const ALLOWED_PARTNERS_CODES = ['BYM', 'LEF', 'SHL'];

const getEnv = (hostname: string): Env => {
  if (STAGING_PATTERN.test(hostname)) return 'staging';
  if (hostname === DEMO_HOSTNAME) return 'demo';
  if (PRODUCTION_HOSTNAMES.includes(hostname)) return 'production';
  return 'development';
};

const isFeatureEnabledForPartner = (allowedCodes: string[]): boolean => {
  if (allowedCodes.length === 0) {
    return false;
  }

  const code = getThreeLetterCode();
  if (!code) {
    return false;
  }

  return allowedCodes.includes(code);
};

/**
 * 実行中のホスト名から環境を判定し、環境別の機能フラグを返します。
 *
 * | 環境 | enableDevelopment | enableStaging | enableInternal |
 * |------|:-:|:-:|:-:|
 * | development | ✅ | ✅ | ✅ |
 * | staging (stg / qa-N) | ❌ | ✅ | ✅ |
 * | demo（allowlist 次第） | ❌ | ❌ | ✅ or ❌ |
 * | production（一般） | ❌ | ❌ | ❌ |
 *
 * - `env`: 現在の環境（`development` | `staging` | `demo` | `production`）
 * - `enableDevelopment`: development 環境で `true`
 * - `enableStaging`: development / staging で `true`（demo / production では `false`）
 * - `enableInternal`: **@deprecated** development / staging で `true` + BYM/LEF/SHL パートナー本番（demo は BYM/LEF/SHL パートナーの場合のみ `true`）。
 *   テナントコードのハードコード依存のため、テナント単位の出し分けは PartnerFeatureFlag へ移行済み（MMT-156）。新規利用は禁止。
 *   本フラグ本体（および `ALLOWED_PARTNERS_CODES` / `isFeatureEnabledForPartner`）と CLAUDE.md 等の記載の削除は LEA-3515 で対応する。
 *
 * 本フラグは「開発中の機能を main にマージし、環境ごとに段階的に有効化する」用途を想定しています。
 * テナント単位での機能出し分け（例: アクティベーションコード）は、ハードコードではなく
 * PartnerFeatureFlag で制御します（MMT-156 で `enableInternal` からの移行を実施済み）。
 *
 * @remarks
 * - SSR/Node 環境では `window` が未定義のため、クライアント実行前提で呼び出してください。
 * - ドメイン構成が変わった場合は `getEnv` のマッピングを更新してください。
 *
 * @returns 環境名と、それに応じたブールフラグのオブジェクト
 */
export const getEnvFeatureFlag: IGetEnvFeatureFlag = () => {
  const hostname = window.location.hostname;
  const env = getEnv(hostname);

  const enableDevelopment = env === 'development';
  const enableStaging = env === 'development' || env === 'staging';
  const enableInternal =
    env === 'development' ||
    env === 'staging' ||
    isFeatureEnabledForPartner(ALLOWED_PARTNERS_CODES);

  return {
    env,
    enableDevelopment,
    enableStaging,
    enableInternal,
  };
};
